import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { CircularProgress, Stack } from '@mui/material';
import { ReportComments } from '../../shared/ReportComments';
import { Snackbar } from '@/components/shared/Snackbar';
import * as state from '@/components/Analysis/state';
import { EquipmentsList } from './EquipmentsList';
import { ReportInternalType } from '@/__generated__/graphql';
import { ComponentReportNavigator } from './ComponentReportNavigator';
import { ReportOrder } from './types';
import { abyssColors } from '@/theme/colors';

type ComponentReportContainerProps = {
  focusAssemblyId?: string;
};

export const ComponentReportContainer = ({ focusAssemblyId }: ComponentReportContainerProps) => {
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const selectedReport = useAtomValue(state.selectedReport);
  const [orderUpdateLoader, setOrderUpdateLoader] = useState<boolean>(false);
  const [orderOfReport, setOrderOfReport] = useState<ReportOrder>(() => {
    const reportOrder = selectedReport?.reportOrder;
    return {
      assemblyOrder: reportOrder?.assemblyOrder?.filter((s): s is string => !!s) || [],
      poiOrder: (reportOrder?.poiOrder as { [key: string]: string[] }) || {},
    };
  });

  useEffect(() => {
    const reportOrder = selectedReport?.reportOrder;
    if (!reportOrder) return;
    setOrderOfReport({
      assemblyOrder: reportOrder.assemblyOrder?.filter((s): s is string => !!s) || [],
      poiOrder: (reportOrder.poiOrder as { [key: string]: string[] }) || {},
    });
  }, [selectedReport?.reportOrder]);

  const { shouldShow, content, persistent, duration } = snackbarMessage;
  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  return (
    <>
      <Stack direction="row">
        <ComponentReportNavigator
          setOrderUpdateLoader={setOrderUpdateLoader}
          orderOfReport={orderOfReport}
          setOrderOfReport={setOrderOfReport}
        />
        {orderUpdateLoader ? (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ width: '100%', minHeight: '100%' }}
          >
            <CircularProgress sx={{ color: abyssColors.primary[300] }} size="10rem" />
          </Stack>
        ) : (
          <EquipmentsList
            reportType={ReportInternalType.Component}
            focusAssemblyId={focusAssemblyId}
          />
        )}
      </Stack>

      <ReportComments />
      <Snackbar
        open={shouldShow}
        onClose={closeSnack}
        persistent={persistent}
        autoHideDuration={duration}
      >
        {content}
      </Snackbar>
    </>
  );
};
